import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { useAuthModal } from '../../features/auth/AuthModalContext';
import { useI18n } from '../../features/i18n';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/how-it-works — marketing page explaining the diner + restaurant
 * value prop. Two tracks (diner / restaurant) toggleable at the top, each
 * with 3 numbered steps in animated cards. Closes with a stats strip and
 * dual CTA row.
 */

const EASE = [0.22, 1, 0.36, 1];

// Visual shape for each step — copy is pulled from translations at render.
const DINER_VISUALS = [
  { id: 'd1', n: 1, icon: 'search', tone: 'blue' },
  { id: 'd2', n: 2, icon: 'chat',   tone: 'green' },
  { id: 'd3', n: 3, icon: 'check',  tone: 'purple' },
];
const RESTAURANT_VISUALS = [
  { id: 'r1', n: 1, icon: 'upload', tone: 'blue' },
  { id: 'r2', n: 2, icon: 'shield', tone: 'green' },
  { id: 'r3', n: 3, icon: 'growth', tone: 'purple' },
];

const TONE_COLORS = {
  blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)',  glow: 'rgba(15,127,232,0.32)' },
  green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)',      glow: 'rgba(22,128,60,0.28)' },
  purple: { bg: '#F1E8FE',                fg: '#5B21B6',               solid: '#7C3AED',               glow: 'rgba(124,58,237,0.28)' },
};

export default function UnifiedHowItWorks() {
  const navigate = useNavigate();
  const { openAuth } = useAuthModal();
  const { t } = useI18n();
  const [track, setTrack] = useState('diner');

  // Only real numbers in the stats strip: the live restaurant total from
  // /listRestaurants. Hidden if it can't be loaded.
  const [restaurantTotal, setRestaurantTotal] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.listRestaurants();
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data?.restaurants || []);
        const total = Number.isFinite(data?.total) ? data.total : rows.length;
        if (total > 0) setRestaurantTotal(total);
      } catch {
        // Leave the strip hidden.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const stats = restaurantTotal
    ? [{ id: 'stat3', value: restaurantTotal.toLocaleString() }]
    : [];

  // Rebuild the step array from visuals + current language each render so
  // switching locale updates titles and bodies without reseed.
  const steps = (track === 'diner' ? DINER_VISUALS : RESTAURANT_VISUALS).map((s) => ({
    ...s,
    title: t(`how.${s.id}_title`),
    body:  t(`how.${s.id}_body`),
  }));

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '36px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 36 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 14 }}>
            <SparkleIcon size={12} /> {t('how.eyebrow')}
          </div>
          <h1 style={{ fontSize: 48, fontWeight: 900, letterSpacing: '-0.028em', color: 'var(--cwm-ink)', margin: '0 0 12px', lineHeight: 1.05 }}>
            {t('how.headline_l1')} <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{t('how.headline_emphasis')}</em>.
          </h1>
          <p style={{ maxWidth: 580, margin: '0 auto', fontSize: 16, fontWeight: 500, color: 'var(--cwm-gray-700)', lineHeight: 1.55 }}>
            {t('how.sub')}
          </p>
        </motion.section>

        {/* Track toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.1, ease: EASE }}
          style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}
        >
          <div style={{
            display: 'inline-flex', padding: 5, borderRadius: 999,
            background: '#fff', border: '1.5px solid var(--cwm-blue-light)',
            boxShadow: '0 8px 24px rgba(13,71,161,0.10)',
          }}>
            <TrackButton active={track === 'diner'}      onClick={() => setTrack('diner')}      label={t('how.track_diner')}      icon="person" />
            <TrackButton active={track === 'restaurant'} onClick={() => setTrack('restaurant')} label={t('how.track_restaurant')} icon="store" />
          </div>
        </motion.div>

        {/* Steps */}
        <div style={{ display: 'grid', gap: 18, marginBottom: 48 }}>
          {steps.map((step, i) => (
            <StepCard key={`${track}-${step.n}`} step={step} index={i} />
          ))}
        </div>

        {/* Stats strip */}
        {stats.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, delay: 0.18, ease: EASE }}
          className="cwm-sheet"
          style={{
            padding: '22px 20px',
            marginBottom: 36,
            background: 'linear-gradient(160deg, #fff, var(--cwm-blue-50))',
          }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 18,
          }}>
            {stats.map((s) => (
              <div key={s.id} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em', color: 'var(--cwm-blue-deep)' }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)', letterSpacing: '0.02em', marginTop: 4 }}>
                  {t(`how.${s.id}_label`)}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        )}

        {/* Value quote */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.56, delay: 0.24, ease: EASE }}
          style={{
            position: 'relative',
            padding: '34px 28px',
            borderRadius: 28,
            background: 'linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
            boxShadow: '0 24px 56px rgba(13,71,161,0.24)',
            color: '#fff',
            textAlign: 'center',
            marginBottom: 36,
            overflow: 'hidden',
          }}
        >
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.3,
          }}/>
          <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
            <QuoteMark />
            <p style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.015em', margin: '14px 0 12px', lineHeight: 1.35 }}>
              Dining out shouldn't start with an apology for your allergies.
            </p>
            <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.85 }}>
              Why we're building Chat With Menu
            </div>
          </div>
        </motion.div>

        {/* Dual CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, delay: 0.3, ease: EASE }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}
        >
          <button className="cwm-btn" onClick={() => openAuth('signup')}>
            {t('how.cta_primary')}
          </button>
          <button className="cwm-btn-secondary" onClick={() => navigate('/unified/for-restaurants')}>
            {t('how.cta_secondary')} <ArrowIcon size={12} />
          </button>
        </motion.div>
      </div>
    </AppShell>
  );
}

// ---------- sub-components ----------

function TrackButton({ active, onClick, label, icon }) {
  return (
    <button type="button" onClick={onClick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 22px', borderRadius: 999,
              background: active ? 'linear-gradient(90deg, #1E9BFF, #0F7FE8)' : 'transparent',
              color: active ? '#fff' : 'var(--cwm-blue-deep)',
              border: 'none', cursor: 'pointer',
              fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 900,
              boxShadow: active ? '0 6px 16px rgba(15,127,232,0.35)' : 'none',
              transition: 'all 180ms ease',
            }}>
      <TrackIcon name={icon} size={14} />
      {label}
    </button>
  );
}

function StepCard({ step, index }) {
  const tone = TONE_COLORS[step.tone] || TONE_COLORS.blue;
  const isEven = index % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, x: isEven ? 24 : -24, y: 14 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ duration: 0.56, delay: 0.18 + index * 0.08, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{
        padding: 28,
        display: 'grid',
        gridTemplateColumns: '72px 1fr',
        gap: 20,
        alignItems: 'start',
      }}
    >
      <div style={{
        width: 72, height: 72, borderRadius: 20,
        display: 'grid', placeItems: 'center', position: 'relative',
        background: `linear-gradient(160deg, #fff, ${tone.bg})`,
        border: `1.5px solid ${tone.solid}33`,
        boxShadow: `0 8px 20px ${tone.glow}`,
        color: tone.solid,
      }}>
        <StepIcon name={step.icon} size={28} />
        <div style={{
          position: 'absolute', top: -8, right: -8,
          width: 28, height: 28, borderRadius: '50%',
          background: tone.solid, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 13, fontWeight: 900, letterSpacing: '-0.02em',
          boxShadow: `0 6px 14px ${tone.glow}`,
        }}>
          {step.n}
        </div>
      </div>
      <div>
        <h3 style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.015em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
          {step.title}
        </h3>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--cwm-gray-700)', fontWeight: 500 }}>
          {step.body}
        </p>
      </div>
    </motion.article>
  );
}

function QuoteMark() {
  return (
    <svg width="36" height="28" viewBox="0 0 36 28" fill="rgba(255,255,255,0.35)" style={{ display: 'block', margin: '0 auto' }}>
      <path d="M14 28V14c0-4.42 3.58-8 8-8V2c-6.627 0-12 5.373-12 12v14h4zm14 0V14c0-4.42 3.58-8 8-8V2c-6.627 0-12 5.373-12 12v14h4z"/>
    </svg>
  );
}

// ---------- icons ----------
function SparkleIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/></svg>; }
function ArrowIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>; }

function TrackIcon({ name, size = 14 }) {
  const paths = {
    person: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0',
    store:  'M3 10l1-6h16l1 6M3 10v10h18V10M3 10h18',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name]}/></svg>;
}

function StepIcon({ name, size = 28 }) {
  const paths = {
    search: 'M11 4a7 7 0 107 7M21 21l-4.5-4.5',
    chat:   'M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    check:  'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
    upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
    growth: 'M3 17l5-5 4 4 7-7M17 9h3v3',
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.search} />
    </svg>
  );
}
